/**
 * CapLedger Supabase Service Layer
 * Provides strongly-typed API wrappers for CapLedger tables and RPCs.
 */

import { supabase } from '@/integrations/supabase/client';
import { logAuditEvent, AUDIT_ACTIONS } from '@/lib/audit';
import { logger } from '@/lib/logger';

// ------------------------------
//  Type definitions
// ------------------------------
export interface Company {
  id: string;
  name: string;
  registration_number?: string;
  created_by: string;
  incorporation_date?: string;
  jurisdiction?: string;
  created_at: string;
  updated_at: string;
}

export interface Shareholder {
  id: string;
  company_id: string;
  name: string;
  email?: string;
  address?: string;
  tax_id?: string;
  entity_type: 'INDIVIDUAL' | 'COMPANY' | 'TRUST';
  created_at: string;
  updated_at: string;
}

export interface LedgerEvent {
  id: string;
  company_id: string;
  shareholder_id: string | null;
  event_type: 'ISSUANCE' | 'TRANSFER' | 'CANCELLATION' | 'SAFE_CONVERSION' | 'ADJUSTMENT';
  shares: number;
  share_class: string;
  price_per_share: number | null;
  note_reference: string | null;
  created_by: string;
  created_at: string;
}

export interface AuditLog {
  id: string;
  user_id: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  company_id: string | null;
  details: any;
  created_at: string;
}

export interface CapTableSummary {
  company_id: string;
  company_name: string;
  shareholder_id: string;
  shareholder_name: string;
  total_shares: number;
}

// ------------------------------
//  Core Service Functions
// ------------------------------

// 🔹 Companies
export async function getCompanies(): Promise<Company[]> {
  const { data, error } = await supabase
    .from('companies')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    logger.error('Failed to fetch companies', error, { action: 'getCompanies' });
    throw error;
  }
  return data ?? [];
}

export async function getCompanyById(id: string): Promise<Company | null> {
  const { data, error } = await supabase.from('companies').select('*').eq('id', id).single();

  if (error) throw error;
  return data;
}

export async function updateCompany(
  id: string,
  updates: Partial<Omit<Company, 'id' | 'created_at' | 'updated_at' | 'created_by'>>
): Promise<Company> {
  const { data, error } = await supabase
    .from('companies')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;

  // Log audit trail
  await logAuditEvent(
    {
      action: AUDIT_ACTIONS.COMPANY_UPDATED,
      entityType: 'company',
      entityId: id,
      details: updates,
    },
    undefined,
    id
  );

  return data;
}

// 🔹 Shareholders
export async function getShareholders(companyId?: string): Promise<Shareholder[]> {
  let query = supabase.from('shareholders').select('*').order('name', { ascending: true });

  if (companyId) {
    query = query.eq('company_id', companyId);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

export async function createShareholder(
  shareholder: Omit<Shareholder, 'id' | 'created_at' | 'updated_at'>
): Promise<Shareholder> {
  const { data, error } = await supabase.from('shareholders').insert(shareholder).select().single();

  if (error) throw error;

  // Log audit trail
  await logAuditEvent(
    {
      action: 'SHAREHOLDER_CREATED',
      entityType: 'shareholder',
      entityId: data.id,
      details: {
        shareholderName: shareholder.name,
        entityType: shareholder.entity_type,
      },
    },
    undefined,
    shareholder.company_id
  );

  return data;
}

export async function updateShareholder(
  id: string,
  updates: Partial<Omit<Shareholder, 'id' | 'created_at' | 'updated_at' | 'company_id'>>
): Promise<Shareholder> {
  const { data, error } = await supabase
    .from('shareholders')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;

  // Log audit trail
  await logAuditEvent(
    {
      action: 'SHAREHOLDER_UPDATED',
      entityType: 'shareholder',
      entityId: id,
      details: updates,
    },
    undefined,
    data.company_id
  );

  return data;
}

export async function deleteShareholder(id: string): Promise<void> {
  // First get the shareholder to access company_id for audit trail
  const { data: shareholder, error: fetchError } = await supabase
    .from('shareholders')
    .select('company_id, name')
    .eq('id', id)
    .single();

  if (fetchError) throw fetchError;

  const { error } = await supabase.from('shareholders').delete().eq('id', id);

  if (error) throw error;

  // Log audit trail
  await logAuditEvent(
    {
      action: 'SHAREHOLDER_DELETED',
      entityType: 'shareholder',
      entityId: id,
      details: {
        shareholderName: shareholder.name,
      },
    },
    undefined,
    shareholder.company_id
  );
}

// 🔹 Ledger Events
export async function getLedgerEvents(companyId?: string): Promise<LedgerEvent[]> {
  let query = supabase.from('ledger_events').select('*').order('created_at', { ascending: false });

  if (companyId) {
    query = query.eq('company_id', companyId);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as LedgerEvent[];
}

// 🔹 Audit Logs
export async function getAuditLogs(companyId?: string): Promise<AuditLog[]> {
  let query = supabase.from('audit_log').select('*').order('created_at', { ascending: false });

  if (companyId) {
    query = query.eq('company_id', companyId);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as AuditLog[];
}

// 🔹 Cap Table Summary
export async function getCapTableSummary(
  companyId?: string,
  stateTypeFilter?: 'LEGAL' | string[]
): Promise<CapTableSummary[]> {
  // If no filter or LEGAL, use cap_table_summary (current ledger state)
  if (!stateTypeFilter || stateTypeFilter === 'LEGAL') {
    let query = supabase.from('cap_table_summary').select('*');

    if (companyId) {
      query = query.eq('company_id', companyId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data ?? [];
  }

  // For SCENARIO/PROJECTION mode, get latest snapshot
  if (Array.isArray(stateTypeFilter) && stateTypeFilter.includes('PROJECTION')) {
    const { data: snapshot, error } = await supabase
      .from('cap_table_snapshots')
      .select('snapshot, created_at')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error || !snapshot) return [];

    // Transform snapshot data to CapTableSummary format
    const snapshotData = snapshot.snapshot as any[];
    return snapshotData.map((entry: any) => ({
      company_id: companyId,
      company_name: '',
      shareholder_id: entry.shareholder_id || null,
      shareholder_name: entry.holder || entry.shareholder || 'Unknown',
      total_shares: entry.shares || 0,
    })) as CapTableSummary[];
  }

  return [];
}

// ------------------------------
//  RPC Wrappers
// ------------------------------

// Insert a ledger event
export async function insertLedgerEvent(params: {
  companyId: string;
  shareholderId: string;
  eventType: LedgerEvent['event_type'];
  shares: number;
  pricePerShare?: number;
  shareClass?: string;
  noteReference?: string;
}): Promise<string> {
  const { data, error } = await supabase.rpc('insert_ledger_event', {
    p_company_id: params.companyId,
    p_shareholder_id: params.shareholderId,
    p_event_type: params.eventType,
    p_shares: params.shares,
    p_price_per_share: params.pricePerShare || null,
    p_share_class: params.shareClass || 'Ordinary',
    p_note_reference: params.noteReference || null,
  });

  if (error) {
    logger.error('Failed to insert ledger event', error, {
      action: 'insertLedgerEvent',
      companyId: params.companyId,
      eventType: params.eventType,
    });
    throw error;
  }

  // Log audit event for ledger transaction
  await logAuditEvent(
    {
      action: AUDIT_ACTIONS.SHARE_ISSUED,
      entityType: 'ledger_event',
      entityId: data,
      details: {
        eventType: params.eventType,
        shares: params.shares,
        shareClass: params.shareClass || 'Ordinary',
        pricePerShare: params.pricePerShare,
      },
    },
    undefined,
    params.companyId
  ).catch((err) => {
    logger.error('Failed to log ledger event audit trail', err);
  });

  logger.info('Ledger event inserted', {
    action: 'insertLedgerEvent',
    companyId: params.companyId,
    eventType: params.eventType,
    shares: params.shares,
  });

  return data;
}

/**
 * Move shares between two holders.
 *
 * A transfer is two ledger rows sharing a `transfer_id`, and they have to be
 * written together — a debit without its credit is not a transfer, it is a
 * cancellation wearing the wrong label. `record_share_transfer` does both in one
 * transaction and refuses to move shares the seller does not hold.
 *
 * Never build a transfer out of `insertLedgerEvent`: a single positive TRANSFER
 * row satisfies `ledger_events_shares_check` and silently creates the shares it
 * was meant to move.
 */
export async function recordShareTransfer(params: {
  companyId: string;
  fromShareholderId: string;
  toShareholderId: string;
  shares: number;
  pricePerShare?: number;
  shareClass?: string;
  noteReference?: string;
}): Promise<{ transfer_id: string; debit_event_id: string; credit_event_id: string }> {
  // Cast because `record_share_transfer` is absent from the generated types:
  // src/integrations/supabase/types.ts has not been regenerated since
  // 20260810090000 added the function and ledger_events.transfer_id. Regenerating
  // is a ~4,600-line change that would bury this one, so it is filed as #78.
  // The RPC does exist — finalize-transfer/index.ts has called it since August.
  const { data, error } = await (supabase.rpc as any)('record_share_transfer', {
    p_company_id: params.companyId,
    p_from_shareholder: params.fromShareholderId,
    p_to_shareholder: params.toShareholderId,
    p_shares: params.shares,
    p_price_per_share: params.pricePerShare ?? null,
    p_share_class: params.shareClass || 'Ordinary',
    p_note_reference: params.noteReference || null,
  });

  if (error) {
    logger.error('Failed to record share transfer', error, {
      action: 'recordShareTransfer',
      companyId: params.companyId,
      shares: params.shares,
    });
    throw error;
  }

  await logAuditEvent(
    {
      action: AUDIT_ACTIONS.SHARE_ISSUED,
      entityType: 'ledger_event',
      entityId: (data as any)?.transfer_id,
      details: {
        eventType: 'TRANSFER',
        shares: params.shares,
        shareClass: params.shareClass || 'Ordinary',
        fromShareholderId: params.fromShareholderId,
        toShareholderId: params.toShareholderId,
      },
    },
    undefined,
    params.companyId
  ).catch((err) => {
    logger.error('Failed to log share transfer audit trail', err);
  });

  logger.info('Share transfer recorded', {
    action: 'recordShareTransfer',
    companyId: params.companyId,
    shares: params.shares,
  });

  return data as any;
}

// Recalculate the cap table for a company
export async function recalcCapTable(companyId: string): Promise<
  {
    shareholder_id: string;
    shareholder_name: string;
    total_shares: number;
  }[]
> {
  const { data, error } = await supabase.rpc('recalculate_cap_table', {
    p_company_id: companyId,
  });

  if (error) throw error;
  return data ?? [];
}

// ------------------------------
//  Utility: subscribe to realtime ledger updates
// ------------------------------
export function subscribeToLedgerEvents(callback: (payload: any) => void, companyId?: string) {
  const channel = supabase.channel('ledger_events');

  const changeConfig: any = {
    event: '*',
    schema: 'public',
    table: 'ledger_events',
  };

  if (companyId) {
    changeConfig.filter = `company_id=eq.${companyId}`;
  }

  return channel.on('postgres_changes', changeConfig, (payload) => callback(payload)).subscribe();
}

// ------------------------------
//  Utility: unsubscribe from channel
// ------------------------------
export function unsubscribeFromChannel(channel: ReturnType<typeof supabase.channel>) {
  return supabase.removeChannel(channel);
}
